import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiX, FiCheck, FiZap, FiSearch, FiShare2 as FiUpload, FiGlobe, FiGrid } from 'react-icons/fi';
import { listSkills, saveSkill, deleteSkill, importSkillsFromText, type AgentSkill, type FieldDef, type FieldType } from './skillsStore';
import { listSites, getSite, type AgentSite } from './sitesStore';
import { composePrompt } from '../../services/browserAgentService';
import { ROUTES } from '../../app/routes';
// Reuse the Claude Agents full-page skills styling (`ca-fp-*` classes).
import '../claudeagents/claudeagents.css';

// Small lightning tile that mirrors the Claude Agents SkillIcon, in the
// Browser Agent orange accent.
const SkillIcon: React.FC<{ size?: number }> = ({ size = 36 }) => (
  <div style={{
    width: size, height: size, borderRadius: 8, flexShrink: 0,
    background: 'rgba(249,115,22,0.10)', border: '1.5px solid rgba(249,115,22,0.30)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f97316',
  }}>
    <svg width={size < 30 ? 13 : 16} height={size < 30 ? 13 : 16} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  </div>
);

const FIELD_STYLE: React.CSSProperties = {
  borderRadius: 6, padding: '7px 9px', background: 'rgba(0,0,0,0.25)',
  border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, outline: 'none',
};
const SEL_STYLE: React.CSSProperties = { ...FIELD_STYLE, background: '#23232b', cursor: 'pointer' };
const OPT_STYLE: React.CSSProperties = { background: '#23232b', color: '#fff' };
const RADIO_LBL: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
};

type Panel =
  | { mode: 'empty' }
  | { mode: 'create' }
  | { mode: 'edit'; id: string };

/**
 * Full-page Browser Agent skills manager at /browser-agent/skills.
 * Mirrors the Claude Agents skills page layout (left list + right editor),
 * but backed by the local browser-agent skills store (name + task prompt + site).
 */
const BrowserAgentSkillsPage: React.FC = () => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState<AgentSkill[]>(() => listSkills());
  const [sites, setSites] = useState<AgentSite[]>(() => listSites());
  const [search, setSearch] = useState('');
  const [panel, setPanel] = useState<Panel>({ mode: 'empty' });
  const [uploadError, setUploadError] = useState('');

  // Editor fields
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [siteId, setSiteId] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Schema-first extraction config
  const [extractMode, setExtractMode] = useState(false);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [output, setOutput] = useState<'table' | 'json'>('table');
  const [maxPages, setMaxPages] = useState<number>(25);

  // AI prompt composer
  const [idea, setIdea] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  // Keep in sync when skills/sites change elsewhere.
  useEffect(() => {
    const hs = () => setSkills(listSkills());
    const hsi = () => setSites(listSites());
    window.addEventListener('ba:skills-changed', hs);
    window.addEventListener('ba:sites-changed', hsi);
    return () => {
      window.removeEventListener('ba:skills-changed', hs);
      window.removeEventListener('ba:sites-changed', hsi);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(s =>
      s.name.toLowerCase().includes(q) || s.prompt.toLowerCase().includes(q));
  }, [skills, search]);

  const siteName = useCallback((id?: string) => (id ? getSite(id)?.name : undefined), []);

  const resetComposer = useCallback(() => { setIdea(''); setGenError(''); setGenerating(false); }, []);

  const openCreate = useCallback(() => {
    setPanel({ mode: 'create' });
    setName('');
    setPrompt('');
    setSiteId('');
    setConfirmDelete(false);
    setUploadError('');
    setExtractMode(false);
    setFields([]);
    setOutput('table');
    setMaxPages(25);
    resetComposer();
    setTimeout(() => promptRef.current?.focus(), 60);
  }, [resetComposer]);

  const openEdit = useCallback((s: AgentSkill) => {
    setPanel({ mode: 'edit', id: s.id });
    setName(s.name);
    setPrompt(s.prompt);
    setSiteId(s.siteId ?? '');
    setConfirmDelete(false);
    setUploadError('');
    setExtractMode(s.kind === 'extract');
    setFields(Array.isArray(s.schema) ? s.schema : []);
    setOutput(s.output === 'json' ? 'json' : 'table');
    setMaxPages(typeof s.maxPages === 'number' ? s.maxPages : 25);
    resetComposer();
  }, [resetComposer]);

  const closeEditor = useCallback(() => {
    setPanel({ mode: 'empty' });
    setName('');
    setPrompt('');
    setSiteId('');
    setConfirmDelete(false);
    setExtractMode(false);
    setFields([]);
    resetComposer();
  }, [resetComposer]);

  // ── schema field-rows builder helpers ──
  const addField = useCallback(() => setFields(f => [...f, { name: '', type: 'string', required: false }]), []);
  const updateField = useCallback((i: number, patch: Partial<FieldDef>) =>
    setFields(f => f.map((fd, idx) => (idx === i ? { ...fd, ...patch } : fd))), []);
  const removeField = useCallback((i: number) => setFields(f => f.filter((_, idx) => idx !== i)), []);

  const cleanFields = useCallback((): FieldDef[] =>
    fields.map(f => ({ ...f, name: f.name.trim() })).filter(f => f.name), [fields]);

  // Expand a short idea into a full task prompt via the backend LLM.
  const handleGenerate = useCallback(async () => {
    const text = idea.trim();
    if (!text || generating) return;
    setGenerating(true);
    setGenError('');
    const sn = siteId ? getSite(siteId)?.name : undefined;
    const res = await composePrompt(text, sn);
    setGenerating(false);
    if (res.prompt) {
      setPrompt(res.prompt);
      if (!name.trim()) setName(text.length <= 60 ? text : text.slice(0, 57) + '…');
    } else {
      setGenError(res.error || 'Could not generate a prompt.');
    }
  }, [idea, generating, siteId, name]);

  // In extract mode a schema is required; the prompt can be light (navigation only).
  const canSave = name.trim().length > 0
    && (extractMode ? cleanFields().length > 0 : prompt.trim().length > 0);

  const handleSave = useCallback(() => {
    if (!canSave) return;
    const id = panel.mode === 'edit' ? panel.id : undefined;
    const saved = saveSkill({
      id, name: name.trim(), prompt: prompt.trim(), siteId: siteId || undefined,
      kind: extractMode ? 'extract' : 'task',
      schema: extractMode ? cleanFields() : undefined,
      output: extractMode ? output : undefined,
      maxPages: extractMode ? maxPages : undefined,
    });
    setSkills(listSkills());
    setPanel({ mode: 'edit', id: saved.id });
  }, [canSave, name, prompt, siteId, extractMode, cleanFields, output, maxPages, panel]);

  const handleDelete = useCallback((id: string) => {
    deleteSkill(id);
    setSkills(listSkills());
    closeEditor();
  }, [closeEditor]);

  // Run a skill in the Browser Agent. If associated with a site, the agent opens
  // that site (and logs in) first; otherwise it pre-fills/runs in the current session.
  // Extract templates also pass their schema so the run pages through & returns records.
  const runSkill = useCallback((p: string, sid?: string) => {
    if (!p.trim() && !(extractMode && cleanFields().length)) return;
    navigate(ROUTES.BROWSER_AGENT);
    const detail: Record<string, any> = { prompt: p.trim(), siteId: sid || undefined };
    if (extractMode && cleanFields().length) {
      detail.schema = cleanFields();
      detail.output = output;
      detail.maxPages = maxPages;
    }
    setTimeout(() => window.dispatchEvent(new CustomEvent('ba:run-skill', { detail })), 120);
  }, [navigate, extractMode, cleanFields, output, maxPages]);

  // Upload: parse a .json / .md / .txt file into one or more skills.
  const onUploadClick = useCallback(() => { setUploadError(''); fileRef.current?.click(); }, []);
  const onFileChosen = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-uploading the same file
    if (!file) return;
    try {
      const text = await file.text();
      const fallbackName = file.name.replace(/\.[^.]+$/, '') || 'Imported skill';
      const created = importSkillsFromText(text, fallbackName);
      setSkills(listSkills());
      const first = created[0];
      if (first) openEdit(first);
    } catch (err: any) {
      setUploadError(err?.message || 'Could not read that file.');
    }
  }, [openEdit]);

  const isEditing = panel.mode === 'create' || panel.mode === 'edit';
  const activeId = panel.mode === 'edit' ? panel.id : null;

  return (
    <div className="ca-fp-page">
      {/* ── LEFT PANEL ──────────────────────────────────────────────────────── */}
      <div className="ca-fp-left">
        <div className="ca-fp-left__header">
          <h2 className="ca-fp-left__title">
            <FiZap size={15} style={{ color: '#f97316' }} /> Skills
            <span className="ca-fp-left__count">{skills.length}</span>
          </h2>
          <div className="ca-fp-left__header-actions">
            <button className="ca-fp-create-btn" onClick={openCreate}>
              <FiPlus size={12} /> Create
            </button>
            <button className="ca-fp-upload-hdr-btn" onClick={onUploadClick}>
              <FiUpload size={12} /> Upload
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,.md,.markdown,.txt,.skill,application/json,text/plain,text/markdown"
              style={{ display: 'none' }}
              onChange={onFileChosen}
            />
          </div>
        </div>

        {uploadError && (
          <div style={{
            margin: '0 12px 8px', padding: '8px 10px', borderRadius: 8, fontSize: 12,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5',
          }}>
            {uploadError}
          </div>
        )}

        {/* Search */}
        <div className="ca-fp-left__search-wrap">
          <FiSearch size={13} className="ca-fp-left__search-icon" />
          <input
            className="ca-fp-left__search"
            placeholder="Search skills…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="ca-fp-left__search-clear" onClick={() => setSearch('')}>
              <FiX size={11} />
            </button>
          )}
        </div>

        {/* Skill list */}
        <div className="ca-fp-left__list">
          {filtered.map(s => {
            const isActive = activeId === s.id;
            const sn = siteName(s.siteId);
            return (
              <div
                key={s.id}
                className={`ca-fp-skill-row${isActive ? ' ca-fp-skill-row--active' : ''}`}
                onClick={() => openEdit(s)}
              >
                <SkillIcon size={36} />
                <div className="ca-fp-skill-row__info">
                  <span className="ca-fp-skill-row__name">
                    {s.name}
                    {s.kind === 'extract' && (
                      <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'rgba(0,176,211,0.15)', border: '1px solid rgba(0,176,211,0.35)', color: '#00B0D3', letterSpacing: '0.03em' }}>
                        EXTRACT{s.schema?.length ? ` · ${s.schema.length}` : ''}
                      </span>
                    )}
                  </span>
                  {sn ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#f97316' }}>
                      <FiGlobe size={10} /> {sn}
                    </span>
                  ) : (
                    <span className="ca-fp-skill-row__desc">{s.prompt}</span>
                  )}
                </div>
                <button
                  className="ca-fp-skill-row__del-btn"
                  title="Delete skill"
                  onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                >
                  <FiTrash2 size={11} />
                </button>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="ca-fp-left__empty">
              {search ? 'No skills match your search.' : 'No skills yet. Create or upload one to get started.'}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL ─────────────────────────────────────────────────────── */}
      <div className="ca-fp-right">
        {!isEditing && (
          <div className="ca-fp-empty-state">
            <div className="ca-fp-empty-state__icon">
              <FiZap size={32} />
            </div>
            <h3>Select a skill to view or edit</h3>
            <p>Choose a skill from the list, or create/upload one. Skills are reusable task
              prompts the Browser Agent runs — optionally on a specific site.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ca-fp-create-btn ca-fp-create-btn--lg" onClick={openCreate}>
                <FiPlus size={14} /> Create New Skill
              </button>
              <button className="ca-fp-upload-hdr-btn" onClick={onUploadClick} style={{ padding: '0 16px' }}>
                <FiUpload size={14} /> Upload
              </button>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="ca-fp-editor">
            <div className="ca-fp-editor__topbar">
              <div className="ca-fp-editor__topbar-left">
                <span className={`ca-fp-editor__badge ${panel.mode === 'create' ? 'ca-fp-editor__badge--new' : 'ca-fp-editor__badge--premade'}`}>
                  {panel.mode === 'create' ? 'New Skill' : 'Edit Skill'}
                </span>
                <input
                  className="ca-fp-editor__name-input"
                  placeholder="Skill name (e.g. Summarize this page)"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  autoFocus={panel.mode === 'create'}
                />
              </div>
              <div className="ca-fp-editor__topbar-actions">
                {panel.mode === 'edit' && (
                  confirmDelete ? (
                    <>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                        Delete this skill?
                      </span>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--save"
                        onClick={() => handleDelete(panel.id)}>
                        <FiCheck size={12} /> Yes, delete
                      </button>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost"
                        onClick={() => setConfirmDelete(false)}>
                        <FiX size={12} /> Cancel
                      </button>
                    </>
                  ) : (
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost"
                      onClick={() => setConfirmDelete(true)}>
                      <FiTrash2 size={13} /> Delete
                    </button>
                  )
                )}
                {!confirmDelete && (
                  <>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost"
                      onClick={closeEditor}>
                      <FiX size={13} /> Close
                    </button>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost"
                      onClick={handleSave} disabled={!canSave}>
                      <FiCheck size={12} /> Save
                    </button>
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--save"
                      onClick={() => { handleSave(); runSkill(prompt, siteId); }}
                      disabled={!(prompt.trim() || (extractMode && cleanFields().length))}>
                      ▶ {extractMode ? 'Run extraction' : 'Run skill'}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="ca-fp-editor__body">
              {/* Site association */}
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
                <FiGlobe size={11} style={{ verticalAlign: '-1px', marginRight: 5, color: '#f97316' }} />
                Site
              </label>
              <select
                value={siteId}
                onChange={e => setSiteId(e.target.value)}
                style={{
                  width: '100%', marginBottom: 16, borderRadius: 8, padding: '9px 11px',
                  background: '#23232b', border: '1px solid rgba(255,255,255,0.18)',
                  color: '#ffffff', fontSize: 13, outline: 'none', cursor: 'pointer',
                  appearance: 'auto',
                }}
              >
                <option value="" style={{ background: '#23232b', color: '#ffffff' }}>
                  No site — run in the current session
                </option>
                {sites.map(s => (
                  <option key={s.id} value={s.id} style={{ background: '#23232b', color: '#ffffff' }}>
                    {s.name}
                  </option>
                ))}
              </select>

              {/* Extract structured data — schema-first template */}
              <div style={{
                marginBottom: 16, padding: 12, borderRadius: 10,
                background: 'rgba(0,176,211,0.06)', border: '1px solid rgba(0,176,211,0.22)',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiGrid size={12} style={{ color: '#00B0D3' }} /> Extract structured data
                  </span>
                  <input type="checkbox" checked={extractMode} onChange={e => setExtractMode(e.target.checked)} />
                </label>

                {extractMode && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
                      Define the columns to pull. The agent pages through the list and returns rows matching this schema.
                    </div>
                    {fields.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                        <input value={f.name} onChange={e => updateField(i, { name: e.target.value })}
                          placeholder="field name" style={{ flex: 2, ...FIELD_STYLE }} />
                        <select value={f.type} onChange={e => updateField(i, { type: e.target.value as FieldType })}
                          style={{ flex: 1, ...SEL_STYLE }}>
                          <option value="string" style={OPT_STYLE}>string</option>
                          <option value="number" style={OPT_STYLE}>number</option>
                          <option value="date" style={OPT_STYLE}>date</option>
                          <option value="boolean" style={OPT_STYLE}>boolean</option>
                        </select>
                        <label title="Required" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                          <input type="checkbox" checked={f.required} onChange={e => updateField(i, { required: e.target.checked })} /> req
                        </label>
                        <button onClick={() => removeField(i)} className="ca-fp-skill-row__del-btn" title="Remove field">
                          <FiTrash2 size={11} />
                        </button>
                      </div>
                    ))}
                    <button onClick={addField} className="ca-fp-editor__btn ca-fp-editor__btn--ghost" style={{ marginTop: 4 }}>
                      <FiPlus size={12} /> Add field
                    </button>

                    <div style={{ display: 'flex', gap: 18, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Output</span>
                        <label style={RADIO_LBL}><input type="radio" checked={output === 'table'} onChange={() => setOutput('table')} /> Table</label>
                        <label style={RADIO_LBL}><input type="radio" checked={output === 'json'} onChange={() => setOutput('json')} /> JSON</label>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                        Max pages
                        <input type="number" min={1} max={200} value={maxPages}
                          onChange={e => setMaxPages(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          style={{ width: 64, ...FIELD_STYLE }} />
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* AI prompt composer — expand a short idea into a full task prompt */}
              <div style={{
                marginBottom: 16, padding: 12, borderRadius: 10,
                background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.22)',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#f9a26c', marginBottom: 7 }}>
                  <FiZap size={12} /> Generate with AI
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={idea}
                    onChange={e => setIdea(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleGenerate(); } }}
                    placeholder="Describe it in a few words, e.g. “get this client’s open policies”"
                    disabled={generating}
                    style={{
                      flex: 1, borderRadius: 8, padding: '9px 11px',
                      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.12)',
                      color: '#fff', fontSize: 13, outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !idea.trim()}
                    className="ca-fp-editor__btn ca-fp-editor__btn--save"
                    style={{ whiteSpace: 'nowrap', opacity: generating || !idea.trim() ? 0.6 : 1 }}
                  >
                    {generating ? 'Generating…' : '✨ Generate'}
                  </button>
                </div>
                {genError && (
                  <div style={{ marginTop: 7, fontSize: 11.5, color: '#fca5a5' }}>{genError}</div>
                )}
                <div style={{ marginTop: 7, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                  Turns a short idea into a polished browser-agent prompt below — then tweak it as needed.
                </div>
              </div>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
                {extractMode ? 'Navigation prompt — how to reach & page the list' : 'Task prompt'}
              </label>
              <textarea
                ref={promptRef}
                className="ca-fp-skill-prompt"
                placeholder={extractMode
                  ? 'How should the agent reach the list and page through it? e.g. “Open the Customers list, then page through every result.”'
                  : 'What should the agent do? It runs this in the session — e.g. “Open the Pricing page and list the plan names as JSON, then finish.”'}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                style={{
                  width: '100%', minHeight: 280, resize: 'vertical', borderRadius: 11,
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--text-primary, #fff)', padding: '14px 16px',
                  fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', outline: 'none',
                }}
              />
              <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                Skills are stored in this browser. “Run skill” opens the Browser Agent; if a site is
                set it opens and logs in there first, otherwise it runs in your active session.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowserAgentSkillsPage;
